import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Loader2, Save, ExternalLink, Truck, Send, CheckCircle2, Eye, ChevronDown, ChevronUp, ChevronRight, Info, Plane, Ship, Scale, Receipt, Store, Table2, RefreshCw, DollarSign, Clock3, Lock, Sparkles, History, Star } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { CostingApproval } from "@/components/CostingApproval";
import { toast } from "sonner";
import { ExcelCostingOverride, saveBlob } from "@/components/ExcelCostingOverride";
import { RateComparison, cfgFor } from "@/components/RateComparison";

function Section({ title, subtitle, icon, step, defaultOpen = false, right, children, testid, open: openProp, onToggle }) {
  const [openState, setOpenState] = useState(defaultOpen);
  const controlled = onToggle != null;
  const open = controlled ? openProp : openState;
  const toggle = controlled ? onToggle : () => setOpenState((o) => !o);
  return (
    <div className={`mb-4 rounded-md border bg-white ${open ? "border-[#EAD9A0] shadow-sm" : "border-border"}`} data-testid={testid}>
      <div className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 transition-colors sm:gap-3 ${open ? "bg-[#FFFBEB]" : ""}`}>
        <button type="button" onClick={toggle} className="flex min-w-[220px] flex-1 items-center gap-2.5 text-left" data-testid={testid ? `${testid}-toggle` : undefined}>
          {open ? <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" /> : <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />}
          {step != null && <span className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${open ? "bg-[#E0B84B] text-white" : "bg-slate-100 text-slate-500"}`}>{step}</span>}
          {icon}
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-slate-700 break-words">{title}</span>
            {subtitle && <span className="block text-[11px] font-normal text-muted-foreground">{subtitle}</span>}
          </span>
        </button>
        {right && open && <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">{right}</div>}
      </div>
      {open && <div className="border-t border-border p-3">{children}</div>}
    </div>
  );
}

const fmtAmt = (cur, v) => {
  if (v == null) return "—";
  const n = Number(v).toLocaleString(undefined, cur === "TZS" ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return cur ? `${cur} ${n}` : n;
};

function BreakdownList({ bd }) {
  if (!bd || !bd.length) return null;
  return (
    <div className="space-y-0.5">
      {bd.map((e, i) => {
        const strong = e.kind === "subtotal" || e.kind === "total";
        return (
          <div key={i} className={`flex items-start justify-between gap-4 rounded px-3 py-1.5 ${e.kind === "subtotal" ? "mt-1 border-t border-border bg-slate-50" : ""} ${e.kind === "total" ? "mt-1 border-t-2 border-[#1B4383] bg-[#EAF1FB]" : ""}`}>
            <div className="min-w-0">
              <div className={`text-xs ${strong ? "font-semibold text-slate-800" : "text-slate-600"}`}>{e.label}</div>
              {e.formula && <div className="text-[11px] text-muted-foreground">{e.formula}</div>}
            </div>
            <div className={`shrink-0 font-mono text-xs ${strong ? "font-semibold text-slate-900" : "text-slate-600"}`}>{fmtAmt(e.cur, e.amount)}</div>
          </div>
        );
      })}
    </div>
  );
}

const INPUT_YELLOW = "bg-[#FFFBEB] border-[#EAD9A0] focus-visible:ring-[#E0B84B]";
// Per-deal editable charge parameters (yellow). Freight is auto-fed from Logistics.
const EDITABLE_PARAMS = new Set(["agent_total", "transport_total", "bank_total", "misc_total", "shipping_line_total"]);
const METHODS = [
  { v: "local", label: "Local (FCA)", icon: Store },
  { v: "air", label: "Air", icon: Plane },
  { v: "road", label: "Road", icon: Truck },
  { v: "sea", label: "Sea", icon: Ship },
];
const tzs = (v) => (v == null ? "—" : `TZS ${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const usd = (v) => (v == null ? "—" : `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtTransit = (q) => {
  const w = q && q.lead_weeks;
  if (w == null || w === "") return (q && q.lead_time) || "—";
  const n = Number(w);
  const days = Math.round(n * 7);
  if (q.lead_unit === "days") return `${days} day${days === 1 ? "" : "s"}`;
  if (Number.isInteger(n)) return `${n} wk${n === 1 ? "" : "s"}`;
  return `${days} day${days === 1 ? "" : "s"}`;
};
const parseLead = (s) => {
  if (!s) return null;
  const str = String(s).toLowerCase();
  const nums = (str.match(/\d+(?:\.\d+)?/g) || []).map(Number);
  if (!nums.length) return null;
  const value = Math.max(...nums);
  const min = Math.min(...nums);
  const unit = /day/.test(str) ? "days" : "weeks";
  return { value, min, unit, days: unit === "days" ? value : value * 7 };
};
const LeadUnitToggle = ({ value, onChange, testid, disabled }) => (
  <div className="inline-flex overflow-hidden rounded-md border border-slate-200 text-[10px]" data-testid={testid}>
    {["days", "weeks"].map((u) => (
      <button key={u} type="button" disabled={disabled} onClick={() => onChange(u)}
        className={`px-2 py-0.5 transition-colors ${value === u ? "bg-[#1B4383] text-white" : "bg-white text-slate-500 hover:bg-slate-50"} ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
        data-testid={`${testid}-${u}`}>{u === "days" ? "Days" : "Weeks"}</button>
    ))}
  </div>
);
const FX_CURRENCIES = ["USD", "ZAR", "EUR", "GBP", "AUD", "AED", "INR", "CNY", "KES", "TZS", "JPY", "CHF"];
// Only lines the supplier actually priced are costed (weights, dims, freight); falls back to all if none priced yet.
const quotedOnly = (rows) => { const all = rows || []; const priced = all.filter((li) => li.unit_price !== null && li.unit_price !== undefined && li.unit_price !== ""); return priced.length ? priced : all; };

// Editable intl parameter fields per method (labels + whether it's a percent).
const PARAM_FIELDS = {
  common: [
    ["discount_pct", "Discount %", true],
    ["freight_total", "Freight (Total)", false], ["packing_total", "Packing (Total)", false],
    ["insurance_rate", "Insurance %", true], ["rdl_rate", "RDL %", true], ["cpf_rate", "CPF %", true],
    ["warehouse_rate", "Warehouse %", true], ["agent_total", "Agent Fee", false],
    ["transport_total", "Transport & Labour", false], ["bank_total", "Bank Fee", false],
    ["misc_total", "Misc", false],
  ],
  sea: [
    ["wharfage_rate", "Wharfage %", true], ["shipping_line_total", "Shipping Line", false],
    ["cipl_rate", "CIPL/PID % (Freight)", true], ["greenhouse_rate", "Greenhouse Levy %", true],
    ["port_rate", "Port Charges %", true],
  ],
};

export default function CostingSection({ rfqId, supplierId, currency, onStatus }) {
  const [method, setMethod] = useState("local");
  const [cfg, setCfg] = useState(null);
  const [items, setItems] = useState([]);
  const [computed, setComputed] = useState(null);
  const [hsUrl, setHsUrl] = useState("");
  const [supplierType, setSupplierType] = useState("international");
  const [quoteCurrency, setQuoteCurrency] = useState("");
  const [fxInfo, setFxInfo] = useState(null);
  const [fxLoading, setFxLoading] = useState(false);
  const [freight, setFreight] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [rateModes, setRateModes] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmedBasis, setConfirmedBasis] = useState(null);
  const [basisHistory, setBasisHistory] = useState([]);
  const [histOpen, setHistOpen] = useState(false);
  const [reactivatingId, setReactivatingId] = useState(null);
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bdRow, setBdRow] = useState(null);
  // Build Costing sub-sections toggle independently; multiple can stay open at once.
  const [openSubs, setOpenSubs] = useState(() => new Set(["dims"]));
  const toggleSub = (k) => setOpenSubs((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const [estimating, setEstimating] = useState(false);
  const [hsBusy, setHsBusy] = useState(false);
  const estimateHs = async () => {
    setHsBusy(true);
    try {
      const { data } = await api.post(`/rfqs/${rfqId}/suppliers/${supplierId}/estimate-hs`, { items: items.map((it) => ({ item_id: it.item_id, description: it.description })) });
      const byId = {};
      (data.estimates || []).forEach((e) => { if (e.hs_code) byId[e.item_id] = e; });
      const n = items.filter((it) => byId[it.item_id] && !(it.hs_code && !it.hs_ai)).length;
      if (!n) { toast.info("No lines to fill — every line already has a confirmed or manual HS code"); return; }
      setItems((prev) => prev.map((it) => {
        const e = byId[it.item_id];
        if (!e || (it.hs_code && !it.hs_ai)) return it;
        return { ...it, hs_code: e.hs_code, duty_rate: e.duty_rate != null ? e.duty_rate / 100 : it.duty_rate, hs_ai: true, hs_heading: e.heading || "" };
      }));
      toast.success(`AI suggested HS codes & duty for ${n} line${n === 1 ? "" : "s"} — check each on the HS Code list, then Confirm`);
    } catch (e) { toast.error(apiError(e)); } finally { setHsBusy(false); }
  };
  const HsCell = ({ it, i, m = "" }) => (
    <div className="space-y-1">
      <Input value={it.hs_code || ""} onChange={(e) => setItem(i, { hs_code: e.target.value, hs_ai: false, hs_source: "" })} className={`h-8 ${it.hs_ai ? "border-amber-400 bg-amber-50" : INPUT_YELLOW}`} placeholder="e.g. 4009.31" title={it.hs_heading || ""} data-testid={`costing-hs${m}-${i}`} />
      {it.hs_source && !it.hs_ai && <div className="text-[11px] font-medium text-[#3F6B50]" title="Filled from the catalog SKU" data-testid={`costing-hs-from-sku${m}-${i}`}>From {it.hs_source}</div>}
      {it.hs_ai && (
        <div className="flex items-center gap-1">
          <span className="rounded bg-amber-100 px-1 text-[10px] font-bold text-amber-800" title={it.hs_heading || "AI estimate"}>AI</span>
          {hsUrl && <a href={hsUrl} target="_blank" rel="noreferrer" className="text-[11px] font-medium text-[#1B4383] hover:underline" data-testid={`costing-hs-check${m}-${i}`}>Check</a>}
          <button type="button" onClick={() => setItem(i, { hs_ai: false })} className="ml-auto inline-flex items-center gap-0.5 rounded bg-[#527D63] px-1.5 py-0.5 text-[11px] font-semibold text-white hover:bg-[#456a54]" data-testid={`costing-hs-confirm${m}-${i}`}><CheckCircle2 className="h-3 w-3" />Confirm</button>
        </div>
      )}
    </div>
  );
  const estimateDims = async () => {
    setEstimating(true);
    try {
      const { data } = await api.post(`/rfqs/${rfqId}/suppliers/${supplierId}/estimate-dims`, {
        items: items.map((it) => ({ item_id: it.item_id, description: it.description, qty: Number(it.qty) || null })),
      });
      const byId = {};
      (data.estimates || []).forEach((e) => { byId[e.item_id] = e; });
      setItems((prev) => prev.map((it) => {
        const e = byId[it.item_id];
        return e ? { ...it, weight_kg: e.weight_kg ?? it.weight_kg, length_cm: e.length_cm ?? it.length_cm, width_cm: e.width_cm ?? it.width_cm, height_cm: e.height_cm ?? it.height_cm, dims_source: "ai" } : it;
      }));
      setParam("dims_unit", "cm");
      toast.success("AI proposed per-unit weights & dimensions (cm) — please review");
    } catch (e) { toast.error(apiError(e)); } finally { setEstimating(false); }
  };
  const [editRates, setEditRates] = useState(false);
  const [destination, setDestination] = useState("Tanzania");
  const [cocThreshold, setCocThreshold] = useState(5000);
  const timer = useRef(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/rfqs/${rfqId}/suppliers/${supplierId}/costing`).then(({ data }) => {
      setHsUrl(data.hs_code_list_url || "");
      const stype = data.supplier_type || "international";
      setSupplierType(stype);
      const qcur = (data.quote_currency || "").toUpperCase();
      setQuoteCurrency(qcur);
      setConfirmedBasis(data.confirmed_basis || null);
      setBasisHistory(data.basis_history || []);
      const c = data.config || { method: stype === "local" ? "local" : "air" };
      setMethod(c.method || (stype === "local" ? "local" : "air"));
      if (stype !== "local" && !c.quote_currency && qcur) c.quote_currency = qcur;
      if (stype !== "local" && (c.fx_to_usd == null) && qcur && qcur !== "USD") fetchFxRate(qcur, false);
      if (c.supplier_lead_weeks == null || c.supplier_lead_weeks === "") {
        let best = null;
        (data.line_items || []).forEach((li) => {
          const p = parseLead(li.lead_time);
          if (p && (!best || p.days > best.days)) best = p;
        });
        if (best) { c.supplier_lead_weeks = best.min; c.supplier_lead_max = best.value > best.min ? best.value : ""; c.supplier_lead_unit = best.unit; }
      }
      setCfg(c);
      setItems(quotedOnly(data.line_items).map((li) => ({
        item_id: li.item_id, description: li.description, qty: li.qty ?? 0,
        unit_price: li.unit_price ?? "", duty_rate: li.duty_rate ?? 0, hs_code: li.hs_code ?? "", hs_ai: !!li.hs_ai, hs_heading: li.hs_heading || "", hs_source: li.hs_source || "",
        weight_kg: li.weight_kg ?? "", length_cm: li.length_cm ?? "",
        width_cm: li.width_cm ?? "", height_cm: li.height_cm ?? "",
        dims_source: li.dims_source || "client", lead_time: li.lead_time ?? "", stock_status: li.stock_status ?? "",
        target_sp_usd: li.target_sp_usd ?? "", target_sp_tzs: li.target_sp_tzs ?? "",
      })));
      setLoading(false);
    }).catch((e) => { toast.error(apiError(e)); setLoading(false); });
  }, [rfqId, supplierId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFreight = useCallback(() => {
    api.get(`/rfqs/${rfqId}/suppliers/${supplierId}/freight-request`)
      .then(({ data }) => { setFreight(data.exists ? data : null); if (data.exists && data.mode !== "delivery" && data.methods) setRateModes(data.methods); }).catch(() => {});
  }, [rfqId, supplierId]);
  useEffect(() => { loadFreight(); }, [loadFreight]);

  useEffect(() => {
    if (onStatus) onStatus({ basisConfirmed: !!confirmedBasis, hasResults: (computed?.rows || []).length > 0 });
  }, [confirmedBasis, computed]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchTo = async (m, amount) => {
    try {
      const c = await cfgFor(m, cfg, method);
      const lc = (freight?.local_costs_by_mode || {})[m];
      const nc = { ...c, freight_total: Number(amount), ...(lc ? { transport_total: lc.transport_total, misc_total: lc.misc_total, local_costs_from_logistics: true } : {}) };
      setMethod(m); setCfg(nc);
      await api.put(`/rfqs/${rfqId}/suppliers/${supplierId}/costing`, { config: { ...nc, coc_threshold: cocThreshold }, items: payloadItems() });
      toast.success(`Costing switched to ${m.toUpperCase()} and saved`);
    } catch { changeMethod(m); }
  };
  const applyRate = async (q) => {
    await selectFreight(freight.id, q.id);
    if (q.method && q.method !== method && freight.mode !== "delivery") await switchTo(q.method, q.amount);
  };
  const selectFreight = async (reqId, quoteId) => {
    try {
      await api.post(`/freight-requests/${reqId}/select`, { quote_id: quoteId });
      toast.success("Freight forwarder selected — fed into costing");
      loadFreight();
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    api.get(`/rfqs/${rfqId}`).then(({ data }) => {
      const dc = data.destination_country || "Tanzania";
      setDestination(dc);
      setCocThreshold(dc === "Uganda" ? 3500 : 5000);
    }).catch(() => {});
  }, [rfqId]);

  const changeDestination = (v) => {
    setDestination(v);
    setCocThreshold(v === "Uganda" ? 3500 : 5000);
    api.put(`/rfqs/${rfqId}/destination`, { destination_country: v }).catch(() => {});
  };

  // When Logistics selects a winning quote, auto-fill the relevant cost parameter
  // (international -> Freight total; local -> In-country delivery total).
  useEffect(() => {
    if (!freight || freight.status !== "selected" || !cfg) return;
    const sel = (freight.forwarder_quotes || []).find((q) => q.id === freight.selected_quote_id);
    if (!sel) return;
    const field = isLocal ? "delivery_total" : "freight_total";
    if (Number(cfg[field]) !== Number(sel.amount)) setParam(field, Number(sel.amount));
    const lc = (freight.local_costs_by_mode || {})[sel.method];
    if (lc && (freight.methods || []).length > 1) {
      if (Number(cfg.transport_total) !== Number(lc.transport_total)) setParam("transport_total", Number(lc.transport_total));
      if (Number(cfg.misc_total) !== Number(lc.misc_total)) setParam("misc_total", Number(lc.misc_total));
    }
  }, [freight]); // eslint-disable-line react-hooks/exhaustive-deps

  const requestFreight = async (mode = "freight") => {
    const dimF = { mm: 0.1, cm: 1, m: 100 }[(cfg && cfg.dims_unit) || "cm"] || 1;
    const vol = (it) => {
      const l = Number(it.length_cm) * dimF, w = Number(it.width_cm) * dimF, h = Number(it.height_cm) * dimF;
      return (!l || !w || !h) ? 0 : (l * w * h) / 6000;
    };
    const perUnit = ((cfg && cfg.weight_basis) || "per_unit") !== "line_total";
    const mult = (it) => (perUnit ? (Number(it.qty) || 1) : 1);
    const actual = items.reduce((s, it) => s + (Number(it.weight_kg) || 0) * mult(it), 0);
    const volTot = items.reduce((s, it) => s + vol(it) * mult(it), 0);
    setRequesting(true);
    const wasHanded = !!(freight && freight.handed_to_ops);
    try {
      // Persist weight & dimensions (and the rest of the costing) so they're saved when sent to Logistics.
      await api.put(`/rfqs/${rfqId}/suppliers/${supplierId}/costing`, { config: { ...cfg, coc_threshold: cocThreshold }, items: payloadItems() });
      const { data } = await api.post(`/rfqs/${rfqId}/suppliers/${supplierId}/freight-request`, {
        method, methods: mode === "delivery" ? [] : (rateModes && rateModes.length ? rateModes : [method]), mode, shipment_type: cfg.shipment_type || "loose",
        total_actual_kg: Number(actual.toFixed(1)), total_vol_kg: Number(volTot.toFixed(1)),
        chargeable_kg: Number(Math.max(actual, volTot).toFixed(1)),
        items: items.map((it) => ({
          item_id: it.item_id, description: it.description, qty: Number(it.qty) || null,
          weight_kg: it.weight_kg === "" ? null : Number(it.weight_kg),
          length_cm: it.length_cm === "" ? null : Number(it.length_cm) * dimF,
          width_cm: it.width_cm === "" ? null : Number(it.width_cm) * dimF,
          height_cm: it.height_cm === "" ? null : Number(it.height_cm) * dimF,
        })),
      });
      setFreight(data);
      toast.success(wasHanded
        ? (mode === "delivery" ? "Sent back to Logistics — reopened for updated delivery quotes" : "Sent back to Logistics — reopened for updated freight quotes")
        : (mode === "delivery" ? "Sent to Logistics — awaiting delivery quotes" : "Sent to Logistics — awaiting freight quotes"));
    } catch (e) { toast.error(apiError(e)); } finally { setRequesting(false); }
  };

  const viewFreightFile = async (fileId) => {
    try {
      const res = await api.get(`/rfqs/${rfqId}/suppliers/${supplierId}/quote-files/${fileId}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open the quote"); }
  };

  const downloadSheet = async () => {
    try {
      const res = await api.get(`/rfqs/${rfqId}/suppliers/${supplierId}/costing/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "Costing_Sheet.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(apiError(e)); }
  };

  const changeMethod = async (m) => {
    setMethod(m);
    try {
      const { data } = await api.get(`/costing/defaults/${m}`);
      setCfg((prev) => ({ ...data, ...(prev && prev.method === m ? prev : {}), method: m }));
    } catch { setCfg({ method: m }); }
  };

  const setParam = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  useEffect(() => {
    if (!cfg) { return; }
    const su = cfg.supplier_lead_unit || "weeks", tu = cfg.transit_unit || "weeks";
    if (su === tu) { return; }
    const v = cfg.transit_weeks_override;
    const conv = v === "" || v == null ? v : (su === "days" ? Math.round(Number(v) * 7) : Math.round((Number(v) / 7) * 10) / 10);
    setCfg((c) => ({ ...c, transit_unit: su, transit_weeks_override: conv }));
  }, [cfg?.supplier_lead_unit, cfg?.transit_unit]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFxRate = async (cur, notify = true) => {
    const c = (cur || quoteCurrency || "").toUpperCase();
    if (!c || c === "USD") { setCfg((p) => ({ ...(p || {}), quote_currency: "USD", fx_to_usd: 1 })); return; }
    setFxLoading(true);
    try {
      const { data } = await api.get(`/fx/rate/${c}`);
      setFxInfo(data);
      if (data.rate_to_usd != null) {
        setCfg((p) => ({ ...(p || {}), quote_currency: c, fx_to_usd: data.rate_to_usd }));
        if (notify) toast.success(`Live rate: 1 ${c} = ${data.rate_to_usd} USD`);
      } else if (notify) {
        toast.error(`Could not fetch a live ${c} rate — enter it manually`);
      }
    } catch { if (notify) toast.error("Live rate lookup failed — enter it manually"); }
    finally { setFxLoading(false); }
  };

  // Auto-reflect the AI-detected / page-level quote currency into the costing FX block.
  useEffect(() => {
    if (loading || !cfg || supplierType === "local") return;
    const cur = (currency || "").toUpperCase();
    if (!cur || (cfg.quote_currency || "").toUpperCase() === cur) return;
    setQuoteCurrency(cur);
    setCfg((p) => ({ ...(p || {}), quote_currency: cur, ...(cur === "USD" ? { fx_to_usd: 1 } : {}) }));
    if (cur !== "USD") fetchFxRate(cur, false);
  }, [currency, loading]); // eslint-disable-line react-hooks/exhaustive-deps
  const setItem = (i, patch) => setItems((p) => p.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  const payloadItems = useCallback(() => items.map((it) => ({
    item_id: it.item_id, description: it.description,
    qty: it.qty === "" ? null : Number(it.qty),
    weight_kg: it.weight_kg === "" ? null : Number(it.weight_kg),
    length_cm: it.length_cm === "" ? null : Number(it.length_cm),
    width_cm: it.width_cm === "" ? null : Number(it.width_cm),
    height_cm: it.height_cm === "" ? null : Number(it.height_cm),
    dims_source: it.dims_source,
    unit_price: it.unit_price === "" ? null : Number(it.unit_price),
    duty_rate: it.duty_rate === "" ? null : Number(it.duty_rate),
    hs_code: it.hs_code || null,
    hs_ai: !!it.hs_ai,
    hs_heading: it.hs_heading || null,
    target_sp_usd: it.target_sp_usd === "" || it.target_sp_usd == null ? null : Number(it.target_sp_usd),
    target_sp_tzs: it.target_sp_tzs === "" || it.target_sp_tzs == null ? null : Number(it.target_sp_tzs),
    lead_time: it.lead_time || "",
    stock_status: it.stock_status || null,
  })), [items]);

  // Live recompute (debounced) whenever config or items change.
  useEffect(() => {
    if (loading || !cfg) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api.post(`/rfqs/${rfqId}/suppliers/${supplierId}/costing/compute`, { config: { ...cfg, coc_threshold: cocThreshold }, items: payloadItems() })
        .then(({ data }) => setComputed(data)).catch(() => {});
    }, 350);
    return () => timer.current && clearTimeout(timer.current);
  }, [cfg, items, loading, rfqId, supplierId, payloadItems, cocThreshold]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/rfqs/${rfqId}/suppliers/${supplierId}/costing`, { config: { ...cfg, coc_threshold: cocThreshold }, items: payloadItems() });
      toast.success("Costing saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const confirmBasis = async () => {
    if (confirmedBasis?.source === "excel" && !window.confirm("This replaces the Excel override with the system-calculated costing. You can switch back from Quote Basis History. Continue?")) return;
    setConfirming(true);
    try {
      // Persist the latest prices/config first, then lock the computed landed/lead.
      await api.put(`/rfqs/${rfqId}/suppliers/${supplierId}/costing`, { config: { ...cfg, coc_threshold: cocThreshold }, items: payloadItems() });
      const { data } = await api.post(`/rfqs/${rfqId}/suppliers/${supplierId}/costing/confirm-basis`);
      setConfirmedBasis(data);
      setBasisHistory(data.basis_history || []);
      toast.success("Locked as the basis of the quote — the Somcore Quote will use these landed prices & lead time");
    } catch (e) { toast.error(apiError(e)); } finally { setConfirming(false); }
  };

  const reactivateBasis = async (entryId) => {
    setReactivatingId(entryId);
    try {
      const { data } = await api.post(`/rfqs/${rfqId}/suppliers/${supplierId}/costing/basis-history/${entryId}/activate`);
      setConfirmedBasis(data);
      setBasisHistory(data.basis_history || []);
      // Revert the Costing Results form to the snapshot that was confirmed.
      const { data: cd } = await api.get(`/rfqs/${rfqId}/suppliers/${supplierId}/costing`);
      const c = cd.config || {};
      setMethod(c.method || (supplierType === "local" ? "local" : "air"));
      setCfg(c);
      setItems(quotedOnly(cd.line_items).map((li) => ({
        item_id: li.item_id, description: li.description, qty: li.qty ?? 0,
        unit_price: li.unit_price ?? "", duty_rate: li.duty_rate ?? 0, hs_code: li.hs_code ?? "", hs_ai: !!li.hs_ai, hs_heading: li.hs_heading || "", hs_source: li.hs_source || "",
        weight_kg: li.weight_kg ?? "", length_cm: li.length_cm ?? "",
        width_cm: li.width_cm ?? "", height_cm: li.height_cm ?? "",
        dims_source: li.dims_source || "client", lead_time: li.lead_time ?? "", stock_status: li.stock_status ?? "",
        target_sp_usd: li.target_sp_usd ?? "", target_sp_tzs: li.target_sp_tzs ?? "",
      })));
      toast.success("Costing reverted to this quote basis snapshot");
    } catch (e) { toast.error(apiError(e)); } finally { setReactivatingId(null); }
  };

  if (loading || !cfg) return null;
  const isLocal = method === "local";
  const isIntl = supplierType !== "local";
  const methodsShown = isIntl ? METHODS.filter((m) => m.v !== "local") : METHODS.filter((m) => m.v === "local");
  const shipType = cfg.shipment_type || "loose";
  const isContainer = shipType === "20ft" || shipType === "40ft";
  const rowsById = {};
  (computed?.rows || []).forEach((r) => { rowsById[r.item_id] = r; });
  const marginTzs = (computed?.rows || []).reduce((s, r) => s + (isLocal ? (r.margin_amt || 0) * (r.qty || 0) : (r.margin || 0) * (cfg?.usd_to_tzs || 0)), 0);
  const totQty = (computed?.rows || []).reduce((s, r) => s + (Number(r.qty) || 0), 0);
  const totCostTzs = (computed?.rows || []).reduce((s, r) => s + ((r.cost_unit_tzs || 0) * (Number(r.qty) || 0)), 0);
  const totDeliveryTzs = (computed?.rows || []).reduce((s, r) => s + ((r.delivery_unit_tzs || 0) * (Number(r.qty) || 0)), 0);
  const totSellingTzs = computed?.totals?.selling_tzs || (computed?.rows || []).reduce((s, r) => s + (r.extended_tzs || 0), 0);
  const realisedPct = totSellingTzs ? (marginTzs / totSellingTzs) * 100 : 0;
  const reverse = cfg?.pricing_mode === "reverse";
  const reverseCcy = cfg?.reverse_ccy || "TZS";
  const spKey = reverseCcy === "USD" ? "target_sp_usd" : "target_sp_tzs";
  const usdTzs = Number(cfg?.usd_to_tzs) || 2650;
  const totLandedUsd = (computed?.rows || []).reduce((s, r) => s + (r.landed_after_finance || 0), 0);
  const totSellingUsd = (computed?.rows || []).reduce((s, r) => s + (r.selling || 0), 0);
  const paramList = [...PARAM_FIELDS.common, ...(method === "sea" ? PARAM_FIELDS.sea : [])];
  const stdParams = paramList.filter(([k]) => k !== "discount_pct");
  const volDivisor = 6000;
  const dimUnit = (cfg && cfg.dims_unit) || "cm";
  const dimFactor = { mm: 0.1, cm: 1, m: 100 }[dimUnit] || 1;
  const volWeight = (it) => {
    const l = Number(it.length_cm) * dimFactor, w = Number(it.width_cm) * dimFactor, h = Number(it.height_cm) * dimFactor;
    if (!l || !w || !h) return null;
    return (l * w * h) / volDivisor;
  };
  const perUnitBasis = ((cfg && cfg.weight_basis) || "per_unit") !== "line_total";
  const wMult = (it) => (perUnitBasis ? (Number(it.qty) || 1) : 1);
  const totActual = items.reduce((s, it) => s + (Number(it.weight_kg) || 0) * wMult(it), 0);
  const totVol = items.reduce((s, it) => s + (volWeight(it) || 0) * wMult(it), 0);
  const totChargeable = Math.max(totActual, totVol);
  const stepKeys = isLocal ? ["dims", "sourcing", "lead", "results"] : ["dims", "sourcing", "params", "lead", "results"];
  const stepNo = (k) => stepKeys.indexOf(k) + 1;
  const finalLead = (() => {
    const sel = freight && freight.status === "selected" ? (freight.forwarder_quotes || []).find((q) => q.id === freight.selected_quote_id) : null;
    const num = (v) => (v !== "" && v != null ? Number(v) : 0);
    const supUnit = cfg.supplier_lead_unit || "weeks";
    const supVal = num(cfg.supplier_lead_weeks);
    const supDays = supUnit === "days" ? supVal : supVal * 7;
    const supMax = num(cfg.supplier_lead_max) > supVal ? num(cfg.supplier_lead_max) : 0;
    const supMaxDays = supUnit === "days" ? supMax : supMax * 7;
    const fromFreight = !!(sel && sel.lead_weeks != null);
    const trUnit = supUnit;
    const trDays = fromFreight ? Number(sel.lead_weeks) * 7 : ((cfg.transit_unit || "weeks") === "days" ? num(cfg.transit_weeks_override) : num(cfg.transit_weeks_override) * 7);
    const cocDays = (!isLocal && computed?.auto?.coc_required) ? 7 : 0;
    const totalDays = Math.round(supDays + trDays + cocDays);
    const maxDays = supMax ? Math.round(supMaxDays + trDays + cocDays) : 0;
    const show = (d) => (supUnit === "days" ? `${Math.round(d)}d` : `${Math.round((d / 7) * 10) / 10}w`);
    const wk = Math.round((totalDays / 7) * 10) / 10;
    const wkMax = Math.round((maxDays / 7) * 10) / 10;
    const text = totalDays > 0 ? (supUnit === "days"
      ? (maxDays ? `${totalDays}–${maxDays} Days` : `${totalDays} Day${totalDays === 1 ? "" : "s"}`)
      : (maxDays ? `${wk}–${wkMax} Weeks` : `${wk} Week${wk === 1 ? "" : "s"}`)) : "—";
    const detail = `Supplier ${show(supDays)}${supMax ? `–${show(supMaxDays)}` : ""} + Transit ${show(trDays)}${!isLocal ? ` + COC ${cocDays ? show(7) : "0"}` : ""}`;
    return { totalDays, text, detail, trUnit };
  })();
  // One unit for the whole lead-time step: switching converts the entered numbers so the duration stays the same.
  const setLeadUnit = (u) => {
    const conv = (v, from) => { if (v === "" || v == null) return v; const d = from === "days" ? Number(v) : Number(v) * 7; return u === "days" ? Math.round(d) : Math.round((d / 7) * 10) / 10; };
    setCfg((c) => ({ ...c, supplier_lead_unit: u, transit_unit: u,
      supplier_lead_weeks: conv(c.supplier_lead_weeks, c.supplier_lead_unit || "weeks"),
      supplier_lead_max: conv(c.supplier_lead_max, c.supplier_lead_unit || "weeks"),
      transit_weeks_override: conv(c.transit_weeks_override, c.transit_unit || "weeks") }));
  };

  return (
    <div className="rounded-lg border border-border bg-white" data-testid="costing-section">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3" data-testid="costing-header">
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${isIntl ? "bg-[#E6ECF5] text-[#1B4383]" : "bg-[#E4EEE8] text-[#527D63]"}`} data-testid="costing-supplier-type">
          {isIntl ? "International Supplier" : "Local Supplier"}
        </span>
      </div>

      {confirmedBasis && (
        <div className="flex items-center gap-2 border-b border-[#CFE0CF] bg-[#F3F8F4] px-5 py-2 text-xs text-[#2F6B45]" data-testid="costing-basis-badge">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span>
            <b>Basis Confirmed</b> · Rev {confirmedBasis.revision}
            {confirmedBasis.source === "excel" ? ` · Excel override${confirmedBasis.file_name ? ` (${confirmedBasis.file_name})` : ""}` : confirmedBasis.pricing_mode === "reverse" ? ` · ${confirmedBasis.reverse_ccy || "TZS"} basis` : ""}
            {confirmedBasis.source !== "excel" && confirmedBasis.blended_margin_pct != null ? ` · Blended ${(confirmedBasis.blended_margin_pct * 100).toFixed(1)}%` : ""}
            {confirmedBasis.confirmed_at ? ` · ${new Date(confirmedBasis.confirmed_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}` : ""}
            {confirmedBasis.confirmed_by_name ? ` · by ${confirmedBasis.confirmed_by_name}` : ""}
            {confirmedBasis.lead ? ` · Lead ${confirmedBasis.lead}` : ""}
            <span className="ml-1 text-[#527D63]">{confirmedBasis.source === "excel" ? "— the Somcore Quote uses the landed costs from the uploaded sheet." : "— the Somcore Quote uses these locked landed prices. Click \"Confirm as Quote Basis\" again to refresh."}</span>
          </span>
        </div>
      )}

      <div className="p-5">
        <Tabs value={method} onValueChange={changeMethod} className="mb-4">
          <TabsList data-testid="costing-method-tabs">
            {methodsShown.map((m) => <TabsTrigger key={m.v} value={m.v} data-testid={`costing-method-${m.v}`}><m.icon className="mr-1.5 h-3.5 w-3.5" />{m.label}</TabsTrigger>)}
          </TabsList>
        </Tabs>

        {(isIntl || isLocal) && (
          <Section title="Weight & Dimensions" subtitle={isLocal ? "Capture item weight & size so delivery can be sized" : "Capture weight & size so freight can be sourced"} step={stepNo("dims")} testid="weight-dims-block" open={openSubs.has("dims")} onToggle={() => toggleSub("dims")}
            icon={<span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-[#EAF1FB] text-[#1B4383]"><Scale className="h-3.5 w-3.5" /></span>}
            right={(
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
                <Button size="sm" variant="outline" className="h-8 w-full sm:w-auto" onClick={estimateDims} disabled={estimating} data-testid="estimate-dims-btn">
                  {estimating ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}Estimate with AI
                </Button>
                <div className="flex items-center gap-2">
                  <Label className="w-24 shrink-0 text-xs sm:w-auto">Weights Are</Label>
                  <Select value={cfg.weight_basis || "per_unit"} onValueChange={(v) => setParam("weight_basis", v)}>
                    <SelectTrigger className="h-8 w-full text-xs sm:w-44" data-testid="weight-basis"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_unit">Per unit (× qty)</SelectItem>
                      <SelectItem value="line_total">Total for the line</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="w-24 shrink-0 text-xs sm:w-auto">Units</Label>
                  <Select value={cfg.dims_unit || "cm"} onValueChange={(v) => setParam("dims_unit", v)}>
                    <SelectTrigger className="h-8 w-full text-xs sm:w-20" data-testid="dims-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mm">mm</SelectItem>
                      <SelectItem value="cm">cm</SelectItem>
                      <SelectItem value="m">m</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {isIntl && (
                  <div className="flex items-center gap-2">
                    <Label className="w-24 shrink-0 text-xs sm:w-auto">Shipment</Label>
                    <Select value={shipType} onValueChange={(v) => setParam("shipment_type", v)}>
                      <SelectTrigger className="h-8 w-full text-xs sm:w-52" data-testid="shipment-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="loose">Loose (by weight/dims)</SelectItem>
                        <SelectItem value="20ft">20ft container</SelectItem>
                        <SelectItem value="40ft">40ft container</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}>
            {isContainer && (
              <p className="mb-2 text-xs text-muted-foreground">Full {shipType} container — freight will be the flat container price from the forwarder. Weight &amp; dims below are still recorded for reference.</p>
            )}
            <div className="hidden overflow-x-auto sm:block">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-slate-500">
                      <th className="px-2 py-1.5 font-medium">Item</th>
                      <th className="px-2 py-1.5 font-medium text-right w-16">Qty</th>
                      <th className="px-2 py-1.5 font-medium w-24">Weight (kg)</th>
                      <th className="px-2 py-1.5 font-medium w-24">L ({dimUnit})</th>
                      <th className="px-2 py-1.5 font-medium w-24">W ({dimUnit})</th>
                      <th className="px-2 py-1.5 font-medium w-24">H ({dimUnit})</th>
                      <th className="px-2 py-1.5 font-medium text-right w-20">Volumetric</th>
                      <th className="px-2 py-1.5 font-medium w-36">Provided by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={it.item_id} className="border-t border-border" data-testid={`dims-row-${i}`}>
                        <td className="px-2 py-1.5 max-w-[200px] truncate text-slate-700">{it.description || "(untitled)"}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-slate-600" data-testid={`dims-qty-${i}`}>{it.qty != null && it.qty !== "" ? it.qty : "—"}</td>
                        <td className="px-2 py-1.5"><Input type="number" value={it.weight_kg} onChange={(e) => setItem(i, { weight_kg: e.target.value })} className={`h-8 ${INPUT_YELLOW}`} data-testid={`dims-weight-${i}`} /></td>
                        <td className="px-2 py-1.5"><Input type="number" value={it.length_cm} onChange={(e) => setItem(i, { length_cm: e.target.value })} className={`h-8 ${INPUT_YELLOW}`} data-testid={`dims-l-${i}`} /></td>
                        <td className="px-2 py-1.5"><Input type="number" value={it.width_cm} onChange={(e) => setItem(i, { width_cm: e.target.value })} className={`h-8 ${INPUT_YELLOW}`} data-testid={`dims-w-${i}`} /></td>
                        <td className="px-2 py-1.5"><Input type="number" value={it.height_cm} onChange={(e) => setItem(i, { height_cm: e.target.value })} className={`h-8 ${INPUT_YELLOW}`} data-testid={`dims-h-${i}`} /></td>
                        <td className="px-2 py-1.5 text-right font-mono text-muted-foreground" data-testid={`dims-vol-${i}`}>{volWeight(it) == null ? "—" : `${volWeight(it).toFixed(1)} kg`}</td>
                        <td className="px-2 py-1.5">
                          <Select value={it.dims_source} onValueChange={(v) => setItem(i, { dims_source: v })}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`dims-source-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="client">Supplier</SelectItem>
                              <SelectItem value="somcore">Somcore Estimate</SelectItem>
                              <SelectItem value="ai">By AI</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            </div>

            {/* Mobile cards */}
            <div className="space-y-2 sm:hidden">
              {items.map((it, i) => (
                <div key={it.item_id} className="rounded-lg border border-border bg-white p-3" data-testid={`dims-card-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 break-words text-sm font-medium text-slate-700">{it.description || "(untitled)"}</div>
                    <span className="shrink-0 font-mono text-xs text-muted-foreground" data-testid={`dims-qty-m-${i}`}>Qty {it.qty != null && it.qty !== "" ? it.qty : "—"}</span>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Weight (kg)</Label><Input type="number" value={it.weight_kg} onChange={(e) => setItem(i, { weight_kg: e.target.value })} className={`h-8 ${INPUT_YELLOW}`} data-testid={`dims-weight-m-${i}`} /></div>
                    <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Volumetric</Label><div className="flex h-8 items-center font-mono text-xs text-muted-foreground" data-testid={`dims-vol-m-${i}`}>{volWeight(it) == null ? "—" : `${volWeight(it).toFixed(1)} kg`}</div></div>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">L ({dimUnit})</Label><Input type="number" value={it.length_cm} onChange={(e) => setItem(i, { length_cm: e.target.value })} className={`h-8 ${INPUT_YELLOW}`} data-testid={`dims-l-m-${i}`} /></div>
                    <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">W ({dimUnit})</Label><Input type="number" value={it.width_cm} onChange={(e) => setItem(i, { width_cm: e.target.value })} className={`h-8 ${INPUT_YELLOW}`} data-testid={`dims-w-m-${i}`} /></div>
                    <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">H ({dimUnit})</Label><Input type="number" value={it.height_cm} onChange={(e) => setItem(i, { height_cm: e.target.value })} className={`h-8 ${INPUT_YELLOW}`} data-testid={`dims-h-m-${i}`} /></div>
                  </div>
                  <div className="mt-2 space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Provided by</Label>
                    <Select value={it.dims_source} onValueChange={(v) => setItem(i, { dims_source: v })}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`dims-source-m-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="client">Supplier</SelectItem>
                        <SelectItem value="somcore">Somcore Estimate</SelectItem>
                        <SelectItem value="ai">By AI</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-2 flex flex-wrap items-center justify-between gap-x-6 gap-y-1 text-[11px] text-muted-foreground">
              <span className="text-slate-400" data-testid="weight-basis-hint">{perUnitBasis ? "Entered figures are per unit — multiplied by qty" : "Entered figures are the total for each line"}</span>
              <span className="flex flex-wrap gap-x-6">
                <span>Actual: <b className="font-mono text-slate-700">{totActual.toFixed(1)} kg</b></span>
                <span>Volumetric: <b className="font-mono text-slate-700">{totVol.toFixed(1)} kg</b></span>
                <span>Chargeable: <b className="font-mono text-slate-700">{totChargeable.toFixed(1)} kg</b></span>
              </span>
            </div>
          </Section>
        )}

        {isIntl && (
          <Section title="Freight Sourcing" subtitle="Send to Logistics & pick the winning forwarder" step={stepNo("sourcing")} testid="freight-panel" open={openSubs.has("sourcing")} onToggle={() => toggleSub("sourcing")}
            icon={<span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-[#EAF1FB] text-[#1B4383]"><Truck className="h-3.5 w-3.5" /></span>}
            right={(
              <>
                {freight && (
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    freight.status === "selected" ? "bg-[#E4EEE8] text-[#527D63]"
                    : freight.status === "quoted" ? "bg-[#F6EDD9] text-[#A87F31]"
                    : "bg-[#E6ECF5] text-[#1B4383]"}`} data-testid="freight-status">
                    {freight.status === "selected" ? "Freight Selected" : freight.status === "quoted" ? `${(freight.forwarder_quotes || []).length} quote(s) in` : "Awaiting Freight Input"}
                  </span>
                )}
                <Button size="sm" variant="outline" onClick={() => requestFreight("freight")} disabled={requesting} data-testid="request-freight-btn">
                  {requesting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                  {freight ? "Update Request" : "Request Freight Quotes"}
                </Button>
              </>
            )}>
            {(() => {
              const cur = rateModes && rateModes.length ? rateModes : [method];
              const toggle = (m) => { const has = cur.includes(m); if (has && cur.length === 1) return; setRateModes(has ? cur.filter((x) => x !== m) : [...cur, m]); };
              return (
                <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="freight-rate-modes">
                  <span className="text-xs font-medium text-slate-600">Rates Needed</span>
                  {METHODS.filter((m) => m.v !== "local").map(({ v, label, icon: Icon }) => {
                    const on = cur.includes(v);
                    return (
                      <button key={v} type="button" onClick={() => toggle(v)} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${on ? "border-[#1B4383] bg-[#1B4383] text-white" : "border-border bg-white text-slate-500 hover:bg-slate-50"}`} data-testid={`freight-rate-mode-${v}`}>
                        <Icon className="h-3.5 w-3.5" />{label}
                      </button>
                    );
                  })}
                </div>
              );
            })()}
            {(() => {
              const sel = freight && freight.status === "selected" ? (freight.forwarder_quotes || []).find((q) => q.id === freight.selected_quote_id) : null;
              return sel && sel.method && sel.method !== method && freight.mode !== "delivery" ? (
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="freight-mode-mismatch">
                  <span>Costing is on <b className="uppercase">{method}</b>, chosen rate is <b className="uppercase">{sel.method}</b>.</span>
                  <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => switchTo(sel.method, sel.amount)} data-testid="freight-mode-switch">Switch To {sel.method.toUpperCase()}</Button>
                </div>
              ) : null;
            })()}
            {freight && freight.handed_to_ops && (() => {
              const sug = (freight.forwarder_quotes || []).find((q) => q.id === freight.suggested_quote_id);
              return (
                sug ? <div className="mb-3 flex items-center gap-2 text-xs text-[#1B4383]" data-testid="freight-handed-banner">
                  <Star className="h-3.5 w-3.5 fill-[#1B4383]" />Logistics suggests <b>{sug.forwarder_name}</b> ({(sug.method || "").toUpperCase()} {sug.currency || "USD"} {Number(sug.amount).toLocaleString()})
                </div> : null
              );
            })()}
            {freight && (freight.forwarder_quotes || []).length > 0 && (
              <RateComparison rfqId={rfqId} supplierId={supplierId} freight={freight} cfg={cfg} method={method} items={payloadItems()} cocThreshold={cocThreshold}
                onUse={(row) => applyRate(row.quote)} />
            )}
            {freight && (freight.forwarder_quotes || []).length > 0 && (() => { const multiRates = (freight.methods || []).length > 1; const body = (
              <div data-testid="freight-quotes-view">
                <div className="mt-3 hidden overflow-x-auto rounded-md border border-border sm:block">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 text-left text-slate-500">
                        <th className="px-3 py-1.5 font-medium">Forwarder</th>
                        <th className="px-3 py-1.5 font-medium text-right">Amount</th>
                        <th className="px-3 py-1.5 font-medium">Transit time</th>
                        <th className="px-3 py-1.5 font-medium">Notes</th>
                        <th className="px-3 py-1.5 font-medium">Quote</th>
                        <th className="px-3 py-1.5 font-medium text-right">Use</th>
                      </tr>
                    </thead>
                    <tbody>
                      {freight.forwarder_quotes.map((q) => {
                        const isSel = q.id === freight.selected_quote_id;
                        const isSuggested = q.id === freight.suggested_quote_id;
                        return (
                          <tr key={q.id} className={`border-t border-border ${isSel ? "bg-[#F3F8F4]" : ""}`} data-testid={`freight-view-quote-${q.id}`}>
                            <td className="px-3 py-1.5 font-medium text-slate-800">{isSel && <CheckCircle2 className="mr-1 inline h-3.5 w-3.5 text-[#527D63]" />}{q.forwarder_name}{q.method && ((freight.methods || []).length > 1 || q.method !== method) && <span className="ml-1.5 rounded-full bg-[#E6ECF5] px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[#1B4383]">{q.method}</span>}{isSel && <span className="ml-1.5 rounded-full bg-[#E4EEE8] px-1.5 py-0.5 text-[10px] font-medium text-[#527D63]">winner</span>}{isSuggested && !isSel && <span className="ml-1.5 rounded-full bg-[#F6EDD9] px-1.5 py-0.5 text-[10px] font-medium text-[#A87F31]">logistics suggests</span>}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{q.currency || "USD"} {Number(q.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                            <td className="px-3 py-1.5 text-muted-foreground">{fmtTransit(q)}</td>
                            <td className="px-3 py-1.5 text-muted-foreground">{q.notes || "—"}</td>
                            <td className="px-3 py-1.5">{q.file_id ? <button onClick={() => viewFreightFile(q.file_id)} className="inline-flex items-center gap-1 text-[#1B4383] hover:underline" data-testid={`freight-view-file-${q.id}`}><Eye className="h-3.5 w-3.5" /> View</button> : <span className="text-muted-foreground">—</span>}</td>
                            <td className="px-3 py-1.5 text-right">
                              {isSel ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-[#E4EEE8] px-2 py-0.5 text-[10px] font-semibold text-[#527D63]"><CheckCircle2 className="h-3 w-3" />In Use</span>
                              ) : (
                                <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => applyRate(q)} data-testid={`freight-use-${q.id}`}>Use This</Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 space-y-2 sm:hidden">
                  {freight.forwarder_quotes.map((q) => {
                    const isSel = q.id === freight.selected_quote_id;
                    const isSuggested = q.id === freight.suggested_quote_id;
                    return (
                      <div key={q.id} className={`rounded-lg border p-3 ${isSel ? "border-[#527D63] bg-[#F3F8F4]" : "border-border bg-white"}`} data-testid={`freight-view-quote-m-${q.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-1.5 font-medium text-slate-800">
                            {isSel && <CheckCircle2 className="h-3.5 w-3.5 text-[#527D63]" />}
                            <span className="break-words">{q.forwarder_name}</span>
                            {q.method && ((freight.methods || []).length > 1 || q.method !== method) && <span className="rounded-full bg-[#E6ECF5] px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[#1B4383]">{q.method}</span>}
                            {isSel && <span className="rounded-full bg-[#E4EEE8] px-1.5 py-0.5 text-[10px] font-medium text-[#527D63]">winner</span>}
                            {isSuggested && !isSel && <span className="rounded-full bg-[#F6EDD9] px-1.5 py-0.5 text-[10px] font-medium text-[#A87F31]">logistics suggests</span>}
                          </div>
                          {isSel ? (
                            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#E4EEE8] px-2 py-0.5 text-[10px] font-semibold text-[#527D63]"><CheckCircle2 className="h-3 w-3" />In Use</span>
                          ) : (
                            <Button size="sm" variant="outline" className="h-7 shrink-0 px-2 text-[11px]" onClick={() => applyRate(q)} data-testid={`freight-use-m-${q.id}`}>Use This</Button>
                          )}
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-2 border-t border-border pt-2 text-xs">
                          <div><div className="text-[11px] text-muted-foreground">Amount</div><div className="font-mono text-slate-800">{q.currency || "USD"} {Number(q.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div></div>
                          <div><div className="text-[11px] text-muted-foreground">Transit Time</div><div className="text-slate-700">{fmtTransit(q)}</div></div>
                          <div className="col-span-2"><div className="text-[11px] text-muted-foreground">Notes</div><div className="break-words text-slate-700">{q.notes || "—"}</div></div>
                        </div>
                        {q.file_id && <button onClick={() => viewFreightFile(q.file_id)} className="mt-2 inline-flex items-center gap-1 text-xs text-[#1B4383] hover:underline" data-testid={`freight-view-file-m-${q.id}`}><Eye className="h-3.5 w-3.5" /> View Quote</button>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ); return multiRates ? <details className="text-xs"><summary className="cursor-pointer font-medium text-[#1B4383]" data-testid="freight-all-quotes-toggle">All Quotes ({freight.forwarder_quotes.length})</summary>{body}</details> : body; })()}
            {freight && freight.status === "selected" && (freight.methods || []).length < 2 && (() => {
              const sel = (freight.forwarder_quotes || []).find((q) => q.id === freight.selected_quote_id);
              return sel ? (
                <div className="mt-2 flex items-center gap-2 rounded-md bg-[#F3F8F4] px-3 py-2 text-xs text-[#2F6B45]" data-testid="freight-selected">
                  <CheckCircle2 className="h-4 w-4" />
                  <span><b>{sel.forwarder_name}</b> — {sel.currency} {Number(sel.amount).toLocaleString()}{fmtTransit(sel) !== "—" ? ` · ${fmtTransit(sel)}` : ""} — fed into Freight (total) below.</span>
                </div>
              ) : null;
            })()}
          </Section>
        )}

        {isLocal && (
          <Section title="In-Country Delivery Sourcing" subtitle="Send to Logistics & pick the delivery partner" step={stepNo("sourcing")} testid="delivery-panel" open={openSubs.has("sourcing")} onToggle={() => toggleSub("sourcing")}
            icon={<span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-[#EAF1FB] text-[#1B4383]"><Truck className="h-3.5 w-3.5" /></span>}
            right={(
              <>
                {freight && (
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    freight.status === "selected" ? "bg-[#E4EEE8] text-[#527D63]"
                    : freight.status === "quoted" ? "bg-[#F6EDD9] text-[#A87F31]"
                    : "bg-[#E6ECF5] text-[#1B4383]"}`} data-testid="delivery-status">
                    {freight.status === "selected" ? "Delivery Selected" : freight.status === "quoted" ? `${(freight.forwarder_quotes || []).length} quote(s) in` : "Awaiting Delivery Input"}
                  </span>
                )}
                <Button size="sm" variant="outline" onClick={() => requestFreight("delivery")} disabled={requesting} data-testid="request-delivery-btn">
                  {requesting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                  {freight ? "Update Request" : "Request Delivery Quotes"}
                </Button>
              </>
            )}>
            <p className="mb-3 text-[11px] text-muted-foreground">Collect in-country delivery quotes from the Logistics desk. The winning amount is spread across the lines by value, at cost.</p>
            {freight && freight.handed_to_ops && (() => {
              const sug = (freight.forwarder_quotes || []).find((q) => q.id === freight.suggested_quote_id);
              return (
                <div className="mb-3 flex items-start gap-2 rounded-md bg-[#EAF1FB] px-3 py-2 text-xs text-[#1B4383]" data-testid="delivery-handed-banner">
                  <Truck className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>Logistics has sent the delivery quotes back for your final selection{sug ? <> — they suggest <b>{sug.forwarder_name}</b> ({sug.currency || "TZS"} {Number(sug.amount).toLocaleString()})</> : ""}. Review below and click <b>Use This</b> to lock in the delivery cost.</span>
                </div>
              );
            })()}
            {freight && (freight.forwarder_quotes || []).length > 0 && (
              <div className="mt-3 overflow-x-auto rounded-md border border-border" data-testid="delivery-quotes-view">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50 text-left text-slate-500">
                      <th className="px-3 py-1.5 font-medium">Delivery Partner</th>
                      <th className="px-3 py-1.5 font-medium text-right">Amount</th>
                      <th className="px-3 py-1.5 font-medium">Transit Time</th>
                      <th className="px-3 py-1.5 font-medium">Notes</th>
                      <th className="px-3 py-1.5 font-medium">Quote</th>
                      <th className="px-3 py-1.5 font-medium text-right">Use</th>
                    </tr>
                  </thead>
                  <tbody>
                    {freight.forwarder_quotes.map((q) => {
                      const isSel = q.id === freight.selected_quote_id;
                      const isSuggested = q.id === freight.suggested_quote_id;
                      return (
                        <tr key={q.id} className={`border-t border-border ${isSel ? "bg-[#F3F8F4]" : ""}`} data-testid={`delivery-view-quote-${q.id}`}>
                          <td className="px-3 py-1.5 font-medium text-slate-800">{isSel && <CheckCircle2 className="mr-1 inline h-3.5 w-3.5 text-[#527D63]" />}{q.forwarder_name}{q.method && ((freight.methods || []).length > 1 || q.method !== method) && <span className="ml-1.5 rounded-full bg-[#E6ECF5] px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[#1B4383]">{q.method}</span>}{isSel && <span className="ml-1.5 rounded-full bg-[#E4EEE8] px-1.5 py-0.5 text-[10px] font-medium text-[#527D63]">winner</span>}{isSuggested && !isSel && <span className="ml-1.5 rounded-full bg-[#F6EDD9] px-1.5 py-0.5 text-[10px] font-medium text-[#A87F31]">logistics suggests</span>}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{q.currency || "TZS"} {Number(q.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                          <td className="px-3 py-1.5 text-muted-foreground">{fmtTransit(q)}</td>
                          <td className="px-3 py-1.5 text-muted-foreground">{q.notes || "—"}</td>
                          <td className="px-3 py-1.5">{q.file_id ? <button onClick={() => viewFreightFile(q.file_id)} className="inline-flex items-center gap-1 text-[#1B4383] hover:underline" data-testid={`delivery-view-file-${q.id}`}><Eye className="h-3.5 w-3.5" /> View</button> : <span className="text-muted-foreground">—</span>}</td>
                          <td className="px-3 py-1.5 text-right">
                            {isSel ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-[#E4EEE8] px-2 py-0.5 text-[10px] font-semibold text-[#527D63]"><CheckCircle2 className="h-3 w-3" />In Use</span>
                            ) : (
                              <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => selectFreight(freight.id, q.id)} data-testid={`delivery-use-${q.id}`}>Use This</Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {freight && freight.status === "selected" && (() => {
              const sel = (freight.forwarder_quotes || []).find((q) => q.id === freight.selected_quote_id);
              return sel ? (
                <div className="mt-2 flex items-center gap-2 rounded-md bg-[#F3F8F4] px-3 py-2 text-xs text-[#2F6B45]" data-testid="delivery-selected">
                  <CheckCircle2 className="h-4 w-4" />
                  <span><b>{sel.forwarder_name}</b> — {sel.currency || "TZS"} {Number(sel.amount).toLocaleString()} — spread across the lines by value (at cost).</span>
                </div>
              ) : null;
            })()}
            {freight && freight.status !== "selected" && (freight.forwarder_quotes || []).length > 0 && (
              <p className="mt-2 text-[11px] text-muted-foreground" data-testid="delivery-await-select">Management selects the winning delivery partner in the Logistics dashboard.</p>
            )}
          </Section>
        )}

        {isLocal ? null : (
          <Section title="Currency & Charges (USD)" subtitle="Convert the supplier price to USD, then duties & landed-cost charges" step={stepNo("params")} testid="charge-params" open={openSubs.has("params")} onToggle={() => toggleSub("params")}
            icon={<span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-[#EAF1FB] text-[#1B4383]"><Receipt className="h-3.5 w-3.5" /></span>}
            right={(
              <>
                <label className="flex items-center gap-1.5 text-xs text-slate-600"><Checkbox checked={!!cfg.is_chemical} onCheckedChange={(v) => setParam("is_chemical", !!v)} data-testid="is-chemical" /> Chemical (GCLA)</label>
                <div className="flex items-center gap-1.5"><Label className="text-xs">Discount %</Label><Input type="number" value={((cfg.discount_pct ?? 0) * 100)} onChange={(e) => setParam("discount_pct", Number(e.target.value) / 100)} className={`h-8 w-20 ${INPUT_YELLOW}`} data-testid="param-discount_pct" /></div>
              </>
            )}>
          <div className="mb-4 rounded-md border border-[#EAD9A0] bg-[#FFFBEB] p-3" data-testid="fx-conversion-block">
            <div className="mb-2 flex items-center gap-2">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-[#1B4383] text-white"><DollarSign className="h-3.5 w-3.5" /></span>
              <span className="text-sm font-semibold text-slate-700">Convert Supplier Price To USD (Costing Base)</span>
            </div>
            <div className="grid grid-cols-2 items-end gap-3 sm:grid-cols-4">
              <div className="space-y-1">
                <Label className="text-xs">Supplier Quoted In</Label>
                <Select value={(cfg.quote_currency || quoteCurrency || "").toUpperCase() || undefined} onValueChange={(v) => { setParam("quote_currency", v); setQuoteCurrency(v); if (v === "USD") setParam("fx_to_usd", 1); else fetchFxRate(v, true); }}>
                  <SelectTrigger className="h-9 bg-white text-xs" data-testid="fx-quote-currency"><SelectValue placeholder="Select currency" /></SelectTrigger>
                  <SelectContent>{FX_CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Rate → USD (1 {(cfg.quote_currency || quoteCurrency) || "unit"} =)</Label>
                <Input type="number" step="0.0001" value={cfg.fx_to_usd ?? ""} onChange={(e) => setParam("fx_to_usd", Number(e.target.value))} className="h-9 bg-white" data-testid="fx-rate-input" placeholder={(cfg.quote_currency || quoteCurrency) === "USD" ? "1" : "e.g. 0.055"} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">&nbsp;</Label>
                <Button type="button" variant="outline" size="sm" className="h-9 w-full bg-white" onClick={() => fetchFxRate(cfg.quote_currency || quoteCurrency, true)} disabled={fxLoading || !(cfg.quote_currency || quoteCurrency) || (cfg.quote_currency || quoteCurrency) === "USD"} data-testid="fx-refresh-btn">
                  {fxLoading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />}Get Live Rate
                </Button>
              </div>
              <div className="text-[11px] text-muted-foreground sm:pb-2">
                {(cfg.quote_currency || quoteCurrency) === "USD" ? "Supplier quoted in USD — no conversion." : fxInfo?.as_of ? `Live rate ${fxInfo.source === "live" ? "as of" : "(cached)"} ${fxInfo.as_of}` : "Pick the quoted currency, then Get Live Rate. Edit if you have a booked rate."}
              </div>
            </div>
          </div>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
              <span className="flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-sm border border-[#EAD9A0] bg-[#FFFBEB]" /> Yellow = per-deal inputs you enter</span>
                <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-sm border border-slate-300 bg-slate-100" /> Grey = standard rates (locked)</span>
              </span>
              <Button type="button" size="sm" variant={editRates ? "default" : "outline"} className="h-7" onClick={() => setEditRates((v) => !v)} data-testid="toggle-edit-rates">
                {editRates ? "Lock Standard Rates" : "Edit Standard Rates"}
              </Button>
            </div>
            <div className="mb-3 rounded-md border border-border bg-slate-50/70 p-3" data-testid="rates-weight-snapshot">
              <div className="mb-1.5 text-[11px] font-semibold tracking-wide text-slate-500">Shipment Snapshot — Reference for Transport, Labour &amp; Misc</div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="text-left text-slate-400">
                      <th className="py-1 pr-3 font-medium">Line Item</th>
                      <th className="py-1 px-2 font-medium text-right">Qty</th>
                      <th className="py-1 px-2 font-medium text-right">Unit Wt</th>
                      <th className="py-1 px-2 font-medium text-right">Line Wt</th>
                      <th className="py-1 pl-2 font-medium text-right">Chargeable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, i) => {
                      const uw = Number(it.weight_kg) || 0;
                      const lineActual = uw * wMult(it);
                      const lineVol = (volWeight(it) || 0) * wMult(it);
                      const ch = Math.max(lineActual, lineVol);
                      return (
                        <tr key={i} className="border-t border-border/60" data-testid={`snapshot-row-${i}`}>
                          <td className="py-1 pr-3 max-w-[220px] truncate text-slate-700">{it.description || "(untitled)"}</td>
                          <td className="py-1 px-2 text-right font-mono">{it.qty != null && it.qty !== "" ? it.qty : "—"}</td>
                          <td className="py-1 px-2 text-right font-mono">{uw ? `${uw} kg` : "—"}</td>
                          <td className="py-1 px-2 text-right font-mono">{lineActual ? `${lineActual.toFixed(1)} kg` : "—"}</td>
                          <td className="py-1 pl-2 text-right font-mono text-slate-700">{ch ? `${ch.toFixed(1)} kg` : "—"}</td>
                        </tr>
                      );
                    })}
                    {!items.length && <tr><td colSpan={5} className="py-2 text-center text-slate-400">No line items yet.</td></tr>}
                  </tbody>
                  {items.length > 0 && (
                    <tfoot>
                      <tr className="border-t border-border font-semibold text-slate-700">
                        <td className="py-1 pr-3">Total</td>
                        <td className="py-1 px-2 text-right font-mono">{items.reduce((s, it) => s + (Number(it.qty) || 0), 0)}</td>
                        <td></td>
                        <td className="py-1 px-2 text-right font-mono">{totActual.toFixed(1)} kg</td>
                        <td className="py-1 pl-2 text-right font-mono">{totChargeable.toFixed(1)} kg</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
              {stdParams.map(([k, lbl, pct]) => {
                const yellow = EDITABLE_PARAMS.has(k);
                const editable = yellow || editRates;
                const isFreight = k === "freight_total";
                return (
                  <div key={k} className="space-y-1">
                    <Label className="text-[11px] text-muted-foreground">{lbl}{isFreight && <span className="ml-1 text-[9px] text-[#1B4383]">(from Logistics)</span>}</Label>
                    <Input type="number" value={pct ? ((cfg[k] ?? 0) * 100) : (cfg[k] ?? "")}
                      onChange={(e) => setParam(k, pct ? Number(e.target.value) / 100 : Number(e.target.value))}
                      disabled={!editable}
                      className={`h-8 text-xs ${yellow ? INPUT_YELLOW : (editRates ? "" : "bg-slate-100 text-slate-500 cursor-not-allowed")}`} data-testid={`param-${k}`} />
                  </div>
                );
              })}
            </div>
            {computed?.auto && (
              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-muted-foreground">
                {"swissport_total" in computed.auto && computed.auto.swissport_total > 0 && <span>Swissport (auto): {usd(computed.auto.swissport_total)}</span>}
                <span>CoC/TBS (auto): {usd(computed.auto.coc_tbs_total)}</span>
                {computed.auto.gcla_total > 0 && <span>GCLA (auto): {usd(computed.auto.gcla_total)}</span>}
                {computed.auto.finance_total > 0 && <span>Finance (auto): {usd(computed.auto.finance_total)}</span>}
              </div>
            )}
          </Section>
        )}

        <Section title="Lead Time to Site" subtitle="Supplier + transit time to the delivery site" step={stepNo("lead")} testid="lead-time-block" open={openSubs.has("lead")} onToggle={() => toggleSub("lead")}
          icon={<span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-[#EAF1FB] text-[#1B4383]"><Clock3 className="h-3.5 w-3.5" /></span>}>
          <div className={`grid grid-cols-1 gap-3 ${isLocal ? "sm:grid-cols-3" : "sm:grid-cols-4"}`}>
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2"><Label className="text-xs">Supplier Lead Time</Label><LeadUnitToggle value={cfg.supplier_lead_unit || "weeks"} onChange={setLeadUnit} testid="lead-supplier-unit" /></div>
              <div className="flex items-center gap-1.5">
                <Input type="number" value={cfg.supplier_lead_weeks ?? ""} onChange={(e) => setParam("supplier_lead_weeks", e.target.value)} placeholder={`e.g. 4`} className={`h-9 ${INPUT_YELLOW}`} data-testid="lead-supplier-weeks" />
                <span className="text-xs text-muted-foreground">to</span>
                <Input type="number" value={cfg.supplier_lead_max ?? ""} onChange={(e) => setParam("supplier_lead_max", e.target.value)} placeholder="Max (optional)" className="h-9" data-testid="lead-supplier-max" />
              </div>
              {cfg.supplier_lead_max !== "" && cfg.supplier_lead_max != null && Number(cfg.supplier_lead_max) <= Number(cfg.supplier_lead_weeks || 0) && <p className="text-[11px] text-amber-700" data-testid="lead-max-warning">Max should be more than the minimum, otherwise it's ignored.</p>}
            </div>
            <div className="space-y-1">
              {(() => { const sel = freight && freight.status === "selected" ? (freight.forwarder_quotes || []).find((q) => q.id === freight.selected_quote_id) : null; const fromFreight = !!(sel && sel.lead_weeks != null); const tu = cfg.supplier_lead_unit || "weeks"; return (<>
                <div className="flex items-center justify-between gap-2"><Label className="text-xs">Transit Time</Label><LeadUnitToggle value={tu} onChange={setLeadUnit} testid="lead-transit-unit" /></div>
                {fromFreight ? (
                  <div className="flex h-9 items-center rounded-md border border-slate-200 bg-white px-3 text-xs text-slate-600" data-testid="lead-transit-weeks">{tu === "days" ? `${Math.round(Number(sel.lead_weeks) * 7)} days` : `${Math.round(Number(sel.lead_weeks) * 10) / 10} weeks`} (from freight)</div>
                ) : (
                  <Input type="number" value={cfg.transit_weeks_override ?? ""} onChange={(e) => setParam("transit_weeks_override", e.target.value)} placeholder={`e.g. 2 ${cfg.supplier_lead_unit || "weeks"}`} className={`h-9 ${INPUT_YELLOW}`} data-testid="lead-transit-weeks" />
                )}
              </>); })()}
            </div>
            <div className="space-y-1"><Label className="text-xs">Destination</Label>
              <Select value={destination} onValueChange={changeDestination}>
                <SelectTrigger className="h-9 bg-white text-xs" data-testid="lead-destination"><SelectValue /></SelectTrigger>
                <SelectContent>{["Tanzania", "Uganda", "Kenya", "DRC", "Zambia", "Rwanda", "Burundi", "Malawi", "Mozambique", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {!isLocal && (
              <div className="space-y-1"><Label className="text-xs">COC (FOB ≥ ${cocThreshold.toLocaleString()})</Label>
                <div className="flex h-9 items-center rounded-md border border-slate-200 bg-white px-3 text-xs" data-testid="lead-coc">{computed?.auto?.coc_required ? <span className="font-medium text-amber-700">Required · +1 wk</span> : <span className="text-slate-400">Not required</span>}</div>
              </div>
            )}
          </div>
          <div className="mt-2 text-sm"><span className="text-muted-foreground">{finalLead.detail} = </span><span className="font-heading text-lg font-bold text-slate-900" data-testid="lead-total-weeks">{finalLead.text}</span>{!isLocal && destination === "Uganda" && <span className="ml-2 text-[11px] text-amber-600">Uganda: COC waived under $3,500 FOB</span>}</div>
        </Section>

        <Section title="Costing Results" subtitle="Set your margin against the landed cost, then Save Costing or Confirm as Quote Basis below" step={stepNo("results")} testid="costing-results-block" open={openSubs.has("results")} onToggle={() => toggleSub("results")}
          icon={<span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-[#EAF1FB] text-[#1B4383]"><Table2 className="h-3.5 w-3.5" /></span>}
          right={(computed?.rows || []).length > 0 ? (
            <Button size="sm" variant="outline" onClick={downloadSheet} data-testid="export-costing-excel-btn"><Table2 className="mr-1.5 h-4 w-4" />Export to Excel</Button>
          ) : null}>
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-[#EAD9A0] bg-[#FFFBEB] px-3 py-2" data-testid="results-margin-block">
          <div className="inline-flex overflow-hidden rounded-md border border-slate-300 text-xs" data-testid="pricing-mode-toggle">
            {[["margin", "Margin → Price"], ["reverse", "Price → Margin"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => { if (v === "reverse") setItems((prev) => prev.map((it) => { const cur = it[spKey]; if (cur !== "" && cur != null) return it; const rr = rowsById[it.item_id]; if (rr?.sp_unit_tzs == null) return it; return { ...it, [spKey]: reverseCcy === "USD" ? +(rr.sp_unit_tzs / usdTzs).toFixed(2) : Math.round(rr.sp_unit_tzs) }; })); setParam("pricing_mode", v); }} className={`px-3 py-1.5 transition-colors ${(cfg.pricing_mode || "margin") === v ? "bg-[#1B4383] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid={`pricing-mode-${v}`}>{l}</button>
            ))}
          </div>
          {!isLocal && (
            <div className="flex items-center gap-1.5"><Label className="whitespace-nowrap text-xs font-semibold text-slate-700">USD → TZS</Label><Input type="number" value={cfg.usd_to_tzs ?? ""} onChange={(e) => setParam("usd_to_tzs", Number(e.target.value))} className={`h-8 w-24 ${INPUT_YELLOW}`} data-testid="intl-usd-tzs" /></div>
          )}
          {reverse ? (
            <>
              <div className="inline-flex overflow-hidden rounded-md border border-slate-300 text-xs" data-testid="reverse-ccy-toggle">
                {["TZS", "USD"].map((cc) => (
                  <button key={cc} type="button" onClick={() => { if (cc !== reverseCcy) setParam("reverse_ccy", cc); }} className={`px-2.5 py-1.5 transition-colors ${reverseCcy === cc ? "bg-[#1B4383] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid={`reverse-ccy-${cc}`}>{cc}</button>
                ))}
              </div>
              <div className="flex items-center gap-1.5"><Label className="whitespace-nowrap text-xs font-semibold text-slate-700">Blended Margin</Label><span className="flex h-8 items-center rounded-md border border-slate-200 bg-slate-50 px-3 font-mono text-xs font-semibold text-[#2F6B45]" data-testid="margin-pct">{realisedPct.toFixed(1)}%</span></div>
              <span className="text-[11px] text-muted-foreground">Type each line's selling price (SP/Unit {reverseCcy}) — implied margin is derived per line. The {reverseCcy === "USD" ? "TZS" : "USD"} set is kept separately.{isLocal ? " Delivery stays at cost." : ""}</span>
            </>
          ) : (
            <>
              <div className="flex items-center gap-1.5"><Label className="whitespace-nowrap text-xs font-semibold text-slate-700">Margin %</Label><Input type="number" min={0} value={cfg.margin_pct === "" || cfg.margin_pct == null ? "" : +(((cfg.margin_pct ?? cfg.markup_pct ?? 0) * 100).toFixed(2))} onChange={(e) => { const raw = e.target.value; if (raw === "") { setParam("margin_pct", ""); return; } let n = Number(raw); if (n < 0) n = 0; setParam("margin_pct", n / 100); }} className={`h-8 w-24 bg-white ${INPUT_YELLOW}`} data-testid="margin-pct" /></div>
              <span className="text-[11px] text-muted-foreground">Margin is on the selling price (Selling = Cost ÷ (1 − Margin%)); at 100%+ it scales as a markup on cost.{isLocal ? " Delivery is added at cost (no margin)." : ""}</span>
            </>
          )}
        </div>
        <div className="hidden overflow-x-auto rounded-md border border-border sm:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs tracking-wide text-slate-500">
                <th className="px-3 py-2.5 font-medium">Item</th>
                <th className="px-3 py-2.5 font-medium w-16">Qty</th>
                <th className="px-3 py-2.5 font-medium w-28">Unit Price</th>
                {!isLocal && <th className="px-3 py-2.5 font-medium w-32">HS Code</th>}
                {!isLocal && <th className="px-3 py-2.5 font-medium w-28">Duty Rate %</th>}
                {!isLocal && <th className="px-3 py-2.5 font-medium text-right">Landed/Unit (USD)</th>}
                {!isLocal && <th className="px-3 py-2.5 font-medium text-right">Landed/Unit (TZS)</th>}
                <th className="px-3 py-2.5 font-medium text-right">{isLocal ? "Cost/Unit (TZS)" : "SP/Unit"}</th>
                {isLocal && <th className="px-3 py-2.5 font-medium text-right">Delivery/Unit (TZS)</th>}
                <th className="px-3 py-2.5 font-medium text-right">{reverse ? `SP/Unit (${reverseCcy})` : "SP/Unit (TZS)"}</th>
                <th className="px-3 py-2.5 font-medium w-28">Ex Works Lead Time</th>
                <th className="px-3 py-2.5 font-medium w-32">Ex Works Dar Lead Time</th>
                <th className="px-3 py-2.5 font-medium text-right">Extended (TZS)</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                const r = rowsById[it.item_id] || {};
                const landedUnit = r.qty ? (r.landed_after_finance || 0) / r.qty : null;
                return (
                  <tr key={it.item_id} className="border-t border-border" data-testid={`costing-row-${i}`}>
                    <td className="px-3 py-2 max-w-[220px]"><div className="flex items-center gap-1.5"><span className="truncate font-medium text-slate-800">{it.description || "(untitled)"}</span>{r.breakdown && <button type="button" onClick={() => setBdRow(r)} className="text-slate-300 transition-colors hover:text-[#1B4383]" title="How this cost is built" data-testid={`breakdown-trigger-${it.item_id}`}><Info className="h-3.5 w-3.5" /></button>}</div></td>
                    <td className="px-3 py-2"><span className="flex h-8 items-center rounded-md border border-slate-200 bg-slate-50 px-2 font-mono text-xs text-slate-600" title="Pulled from the supplier quote above" data-testid={`costing-qty-${i}`}>{(r.qty ?? it.qty) != null && (r.qty ?? it.qty) !== "" ? (r.qty ?? it.qty) : "—"}</span></td>
                    <td className="px-3 py-2"><span className="flex h-8 items-center justify-end rounded-md border border-slate-200 bg-slate-50 px-2 font-mono text-xs text-slate-600" title="Pulled from the supplier quote above — change it in the Verify Prices step" data-testid={`costing-price-${i}`}>{it.unit_price === "" || it.unit_price == null ? "—" : Number(it.unit_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></td>
                    {!isLocal && (
                      <td className="px-3 py-2">{HsCell({ it, i })}</td>
                    )}
                    {!isLocal && (
                      <td className="px-3 py-2"><Input type="number" value={(Number(it.duty_rate || 0) * 100)} onChange={(e) => setItem(i, { duty_rate: Number(e.target.value) / 100 })} className={`h-8 ${INPUT_YELLOW}`} placeholder="%" data-testid={`costing-duty-${i}`} /></td>
                    )}
                    {!isLocal && <td className="px-3 py-2 text-right font-mono text-xs">{landedUnit == null ? "—" : usd(landedUnit)}</td>}
                    {!isLocal && <td className="px-3 py-2 text-right font-mono text-xs">{landedUnit == null ? "—" : tzs(landedUnit * usdTzs)}</td>}
                    <td className="px-3 py-2 text-right font-mono text-xs">{isLocal ? tzs(r.cost_unit_tzs) : usd(r.sp_unit)}</td>
                    {isLocal && <td className="px-3 py-2 text-right font-mono text-xs text-[#1B4383]" title="In-country delivery apportioned to this line by value share, added at cost (no margin)">{r.delivery_unit_tzs ? `TZS ${Number(r.delivery_unit_tzs).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}</td>}
                    <td className="px-3 py-2 text-right">
                      {reverse ? (
                        <div className="flex flex-col items-end">
                          <Input type="number" value={it[spKey] ?? ""} onChange={(e) => setItem(i, { [spKey]: e.target.value })} className={`h-8 w-28 text-right ${INPUT_YELLOW}`} placeholder={r.sp_unit_tzs != null ? (reverseCcy === "USD" ? (r.sp_unit_tzs / usdTzs).toFixed(2) : String(Math.round(r.sp_unit_tzs))) : "0"} data-testid={`target-sp-${i}`} />
                          {r.implied_margin_pct != null && <span className="mt-0.5 text-[10px] text-muted-foreground" data-testid={`implied-margin-${i}`}>{(r.implied_margin_pct * 100).toFixed(1)}% margin</span>}
                        </div>
                      ) : (
                        <span className="font-mono text-xs">{tzs(r.sp_unit_tzs)}</span>
                      )}
                    </td>
                    <td className="px-3 py-2"><Input value={it.lead_time || ""} onChange={(e) => setItem(i, { lead_time: e.target.value })} className={`h-8 w-28 ${INPUT_YELLOW}`} placeholder="e.g. 4 weeks" data-testid={`costing-lead-${i}`} /></td>
                    <td className="px-3 py-2 text-xs font-medium text-slate-700" data-testid={`costing-exw-dar-${i}`}>{finalLead.text}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs font-semibold text-slate-800">{tzs(r.extended_tzs)}</td>
                  </tr>
                );
              })}
              {!items.length && <tr><td colSpan={12} className="px-3 py-8 text-center text-xs text-muted-foreground">No priced line items yet — enter supplier prices above first.</td></tr>}
            </tbody>
            {(computed?.rows || []).length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-[#1B4383] bg-slate-50 font-semibold text-slate-800" data-testid="costing-totals-row">
                  <td className="px-3 py-2 text-xs tracking-wide text-slate-500">Totals</td>
                  <td className="px-3 py-2 font-mono text-xs" data-testid="total-qty">{totQty}</td>
                  <td className="px-3 py-2" />
                  {!isLocal && <td className="px-3 py-2" />}
                  {!isLocal && <td className="px-3 py-2" />}
                  {!isLocal && <td className="px-3 py-2 text-right font-mono text-xs" data-testid="total-landed-usd">{usd(totLandedUsd)}</td>}
                  {!isLocal && <td className="px-3 py-2 text-right font-mono text-xs" data-testid="total-landed-tzs">{tzs(totLandedUsd * usdTzs)}</td>}
                  <td className="px-3 py-2 text-right font-mono text-xs" data-testid="total-cost-tzs">{isLocal ? tzs(totCostTzs) : usd(totSellingUsd)}</td>
                  {isLocal && <td className="px-3 py-2 text-right font-mono text-xs text-[#1B4383]" data-testid="total-delivery-tzs">{tzs(totDeliveryTzs)}</td>}
                  <td className="px-3 py-2 text-right font-mono text-xs" data-testid="total-sp-tzs">{tzs(totSellingTzs)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs font-bold" data-testid="total-extended-tzs">{tzs(totSellingTzs)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Mobile cards */}
        <div className="space-y-2 sm:hidden">
          {items.map((it, i) => {
            const r = rowsById[it.item_id] || {};
            const landedUnit = r.qty ? (r.landed_after_finance || 0) / r.qty : null;
            return (
              <div key={it.item_id} className="rounded-lg border border-border bg-white p-3" data-testid={`costing-card-${i}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 break-words font-medium text-slate-800">{it.description || "(untitled)"}</div>
                  {r.breakdown && <button type="button" onClick={() => setBdRow(r)} className="shrink-0 text-slate-300 hover:text-[#1B4383]" title="How this cost is built" data-testid={`breakdown-trigger-m-${it.item_id}`}><Info className="h-4 w-4" /></button>}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <div><div className="text-[11px] text-muted-foreground">Qty</div><div className="font-mono text-slate-700" data-testid={`costing-qty-m-${i}`}>{(r.qty ?? it.qty) != null && (r.qty ?? it.qty) !== "" ? (r.qty ?? it.qty) : "—"}</div></div>
                  <div><div className="text-[11px] text-muted-foreground">Unit Price</div><div className="font-mono text-slate-700" data-testid={`costing-price-m-${i}`}>{it.unit_price === "" || it.unit_price == null ? "—" : Number(it.unit_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
                </div>
                {!isLocal && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">HS Code</Label>{HsCell({ it, i, m: "-m" })}</div>
                    <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Duty Rate %</Label><Input type="number" value={(Number(it.duty_rate || 0) * 100)} onChange={(e) => setItem(i, { duty_rate: Number(e.target.value) / 100 })} className={`h-8 ${INPUT_YELLOW}`} placeholder="%" data-testid={`costing-duty-m-${i}`} /></div>
                  </div>
                )}
                <div className="mt-2 grid grid-cols-2 gap-2 border-t border-border pt-2 text-xs">
                  {!isLocal && <div><div className="text-[11px] text-muted-foreground">Landed/Unit (USD)</div><div className="font-mono text-slate-700">{landedUnit == null ? "—" : usd(landedUnit)}</div></div>}
                  {!isLocal && <div><div className="text-[11px] text-muted-foreground">Landed/Unit (TZS)</div><div className="font-mono text-slate-700">{landedUnit == null ? "—" : tzs(landedUnit * usdTzs)}</div></div>}
                  <div><div className="text-[11px] text-muted-foreground">{isLocal ? "Cost/Unit (TZS)" : "SP/Unit"}</div><div className="font-mono text-slate-700">{isLocal ? tzs(r.cost_unit_tzs) : usd(r.sp_unit)}</div></div>
                  {isLocal && <div><div className="text-[11px] text-muted-foreground">Delivery/Unit (TZS)</div><div className="font-mono text-[#1B4383]">{r.delivery_unit_tzs ? `TZS ${Number(r.delivery_unit_tzs).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}</div></div>}
                </div>
                <div className="mt-2 space-y-1">
                  <Label className="text-[11px] text-muted-foreground">{reverse ? `SP/Unit (${reverseCcy})` : "SP/Unit (TZS)"}</Label>
                  {reverse ? (
                    <>
                      <Input type="number" value={it[spKey] ?? ""} onChange={(e) => setItem(i, { [spKey]: e.target.value })} className={`h-8 text-right ${INPUT_YELLOW}`} placeholder={r.sp_unit_tzs != null ? (reverseCcy === "USD" ? (r.sp_unit_tzs / usdTzs).toFixed(2) : String(Math.round(r.sp_unit_tzs))) : "0"} data-testid={`target-sp-m-${i}`} />
                      {r.implied_margin_pct != null && <span className="text-[10px] text-muted-foreground" data-testid={`implied-margin-m-${i}`}>{(r.implied_margin_pct * 100).toFixed(1)}% margin</span>}
                    </>
                  ) : (
                    <div className="font-mono text-xs text-slate-700">{tzs(r.sp_unit_tzs)}</div>
                  )}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Ex Works Lead Time</Label><Input value={it.lead_time || ""} onChange={(e) => setItem(i, { lead_time: e.target.value })} className={`h-8 ${INPUT_YELLOW}`} placeholder="e.g. 4 weeks" data-testid={`costing-lead-m-${i}`} /></div>
                  <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Ex Works Dar Lead Time</Label><div className="flex h-8 items-center rounded-md border border-slate-200 bg-slate-50 px-2 text-xs font-medium text-slate-700" data-testid={`costing-exw-dar-m-${i}`}>{finalLead.text}</div></div>
                </div>
                <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-xs">
                  <span className="text-[11px] tracking-wide text-muted-foreground">Extended (TZS)</span>
                  <span className="font-mono font-semibold text-slate-800">{tzs(r.extended_tzs)}</span>
                </div>
              </div>
            );
          })}
          {!items.length && <div className="rounded-lg border border-dashed border-border p-6 text-center text-xs text-muted-foreground">No priced line items yet — enter supplier prices above first.</div>}
          {(computed?.rows || []).length > 0 && (
            <div className="rounded-lg border-2 border-[#1B4383] bg-slate-50 p-3" data-testid="costing-totals-card">
              <div className="flex items-center justify-between text-xs"><span className="text-[11px] tracking-wide text-slate-500">Total Qty</span><span className="font-mono font-semibold text-slate-800">{totQty}</span></div>
              {!isLocal && <div className="mt-1 flex items-center justify-between text-xs"><span className="text-[11px] tracking-wide text-slate-500">Total Landed (USD)</span><span className="font-mono text-slate-800">{usd(totLandedUsd)}</span></div>}
              <div className="mt-1 flex items-center justify-between text-xs"><span className="text-[11px] tracking-wide text-slate-500">{isLocal ? "Total Cost (TZS)" : "Total Selling (USD)"}</span><span className="font-mono text-slate-800">{isLocal ? tzs(totCostTzs) : usd(totSellingUsd)}</span></div>
              {isLocal && <div className="mt-1 flex items-center justify-between text-xs"><span className="text-[11px] tracking-wide text-slate-500">Total Delivery (TZS)</span><span className="font-mono text-[#1B4383]">{tzs(totDeliveryTzs)}</span></div>}
              <div className="mt-1 flex items-center justify-between border-t border-slate-300 pt-1 text-xs"><span className="text-[11px] tracking-wide text-slate-500">Total Selling (TZS)</span><span className="font-mono font-bold text-slate-900">{tzs(totSellingTzs)}</span></div>
            </div>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          {!isLocal && (
            <div className="flex flex-wrap items-center gap-3">
              <Button size="sm" variant="outline" onClick={estimateHs} disabled={hsBusy || !items.length} className="h-8 border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="estimate-hs-btn">
                {hsBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}AI Estimate HS Codes
              </Button>
              {items.some((it) => it.hs_ai) && <span className="text-[11px] font-medium text-amber-700" data-testid="hs-unconfirmed-count">{items.filter((it) => it.hs_ai).length} to confirm</span>}
              {hsUrl && (
                <a href={hsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#1B4383] hover:underline" data-testid="hs-code-link">
                  <ExternalLink className="h-3.5 w-3.5" /> Check the HS Code list for the correct duty rate
                </a>
              )}
            </div>
          )}
          <div className="ml-auto flex flex-wrap items-end justify-end gap-6">
            {(computed?.rows || []).length > 0 && (
              <div className="text-right" data-testid="realised-margin-block">
                <div className="text-xs tracking-wide text-muted-foreground">Realised Margin (after delivery)</div>
                <div className="font-heading text-lg font-bold text-[#2F6B45]" data-testid="realised-margin">{realisedPct.toFixed(1)}% · TZS {marginTzs.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
              </div>
            )}
            <div className="text-right">
              <div className="text-xs tracking-wide text-muted-foreground">Total Selling (TZS)</div>
              <div className="font-heading text-2xl font-bold text-slate-900" data-testid="costing-total-tzs">{tzs(computed?.totals?.selling_tzs)}</div>
            </div>
          </div>
        </div>
        </Section>
      </div>

      {basisHistory.length > 0 && (
        <div className="border-t border-border" data-testid="basis-history">
          <button type="button" onClick={() => setHistOpen((o) => !o)} className="flex w-full items-center justify-between px-5 py-3 text-left" data-testid="basis-history-toggle">
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-700"><History className="h-4 w-4 text-[#1B4383]" />Quote Basis History<span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">{basisHistory.length}</span></span>
            {histOpen ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
          </button>
          {histOpen && (
            <div className="space-y-2 px-5 pb-4">
              {basisHistory.map((h) => (
                <div key={h.id} className={`flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2.5 text-xs ${h.active ? "border-[#1B4383] bg-[#EEF3FA]" : "border-slate-200 bg-white"}`} data-testid={`basis-history-row-${h.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-semibold ${h.source === "excel" ? "bg-[#FFF3D6] text-[#8A6418]" : h.pricing_mode !== "reverse" ? "bg-slate-100 text-slate-600" : h.reverse_ccy === "USD" ? "bg-[#E6ECF5] text-[#1B4383]" : "bg-[#E4EEE8] text-[#527D63]"}`} data-testid={`basis-kind-${h.id}`}>{h.source === "excel" ? "Excel override" : h.pricing_mode === "reverse" ? `${h.reverse_ccy} basis` : "Margin basis"}</span>
                    {h.source === "excel" && h.file_name && <button type="button" onClick={async () => { try { saveBlob(await api.get(`/rfqs/${rfqId}/suppliers/${supplierId}/costing/excel-file/${h.id}`, { responseType: "blob" }), h.file_name); } catch (e) { toast.error(apiError(e)); } }} className="text-[#1B4383] underline-offset-2 hover:underline" data-testid={`basis-file-${h.id}`}>{h.file_name}</button>}
                    <span className="text-slate-600">{h.confirmed_at ? new Date(h.confirmed_at).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                    {h.confirmed_by_name && <span className="text-slate-400">· {h.confirmed_by_name}</span>}
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    {h.source === "excel" ? <span className="text-slate-500">{h.excel_rows} item(s) from sheet</span> : <span className="text-slate-500">Blended <b className="text-[#2F6B45]">{((h.blended_margin_pct || 0) * 100).toFixed(1)}%</b></span>}
                    <span className="font-mono text-slate-700">{tzs(h.total_selling_tzs)}</span>
                    {h.active ? (
                      <span className="inline-flex items-center gap-1 font-semibold text-[#1B4383]" data-testid={`basis-active-${h.id}`}><CheckCircle2 className="h-3.5 w-3.5" />Active Basis</span>
                    ) : (
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => reactivateBasis(h.id)} disabled={reactivatingId === h.id} data-testid={`basis-activate-${h.id}`}>{reactivatingId === h.id ? "Switching..." : "Make Active"}</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-border px-5 py-4" data-testid="costing-footer-actions">
        <CostingApproval rfqId={rfqId} supplierId={supplierId} beforeRequest={save} />
        <ExcelCostingOverride rfqId={rfqId} supplierId={supplierId} onApplied={(d) => { setConfirmedBasis(d); setBasisHistory(d.basis_history || []); setHistOpen(true); }} />
        <Button variant="outline" onClick={save} disabled={saving} data-testid="costing-save"><Save className="mr-2 h-4 w-4" />{saving ? "Saving..." : "Save Costing"}</Button>
        <Button onClick={confirmBasis} disabled={confirming} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid="costing-confirm-basis"><Lock className="mr-2 h-4 w-4" />{confirming ? "Confirming..." : "Confirm as Quote Basis"}</Button>
      </div>

      <Sheet open={!!bdRow} onOpenChange={(o) => !o && setBdRow(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md" data-testid="breakdown-sheet">
          <SheetHeader>
            <SheetTitle>How This Cost Is Built</SheetTitle>
            <SheetDescription>{bdRow?.description || "Line item"}</SheetDescription>
          </SheetHeader>
          <div className="mt-4"><BreakdownList bd={bdRow?.breakdown} /></div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
